/**
 * Accept a workspace/org invite after SSO login.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';

export default function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<{
    organization_name?: string;
    workspace_name?: string | null;
    usable?: boolean;
    status?: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      const res = await apiClient.getInvitePreview(token);
      if (res.success && res.data) setPreview(res.data);
      else setError(res.error || 'Invite not found');
    })();
  }, [token]);

  useEffect(() => {
    if (!authLoading && !user && token) {
      sessionStorage.setItem('pending_invite_token', token);
    }
  }, [authLoading, user, token]);

  const accept = async () => {
    if (!token) return;
    if (!user) {
      navigate('/auth/login');
      return;
    }
    setAccepting(true);
    setError(null);
    const res = await apiClient.acceptInvite(token);
    setAccepting(false);
    if (res.success) {
      sessionStorage.removeItem('pending_invite_token');
      setDone(true);
      setTimeout(() => navigate('/courses'), 1500);
    } else {
      setError(res.error || 'Could not accept invite');
    }
  };

  if (authLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-16">
      <div className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm text-center space-y-4">
        <p className="text-xs font-medium uppercase tracking-wide text-amber-700">Invitation</p>
        <h1 className="text-xl font-bold text-slate-900">Join a learning space</h1>
        {preview && (
          <p className="text-sm text-slate-600">
            <span className="font-semibold">{preview.organization_name}</span>
            {preview.workspace_name ? (
              <> · workspace <span className="font-semibold">{preview.workspace_name}</span></>
            ) : null}
          </p>
        )}
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        {done ? (
          <p className="text-sm text-emerald-700 font-medium">Joined! Redirecting to courses…</p>
        ) : (
          <>
            {!user && (
              <p className="text-sm text-slate-500">
                Sign in with SSO to accept this invite.
              </p>
            )}
            {preview && !preview.usable && (
              <p className="text-sm text-amber-700">This invite is no longer usable ({preview.status}).</p>
            )}
            <div className="flex flex-col gap-2 pt-2">
              {user ? (
                <button
                  type="button"
                  disabled={accepting || !preview?.usable}
                  onClick={accept}
                  className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {accepting ? 'Joining…' : 'Accept invite'}
                </button>
              ) : (
                <Link
                  to="/auth/login"
                  className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
                >
                  Continue with SSO
                </Link>
              )}
              <Link to="/dashboard" className="text-sm text-slate-500 underline">
                Back to dashboard
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
